/**
 * DATES
 *
 * The one place for date handling. Two kinds of value, kept apart:
 *
 * 1. CALENDAR DATES: "yyyy-mm-dd" strings. serviceDate, startDate, endDate,
 *    dateOfBirth, terminationDate, effectiveDate... A calendar date names a
 *    day, not a moment. It never becomes a Date: math is done on the year,
 *    month and day numbers, and the result goes straight back to a string.
 *    `new Date("2026-05-04")` is midnight UTC, which is the evening of 5/3
 *    anywhere in the US.
 *
 *    Zero-padded "yyyy-mm-dd" strings sort in date order, so once a value has
 *    been through toDateString(), comparing is a plain string comparison:
 *
 *      toDateString(user.terminationDate) <= todayDateString()
 *
 * 2. INSTANTS: fields ending in Ts, epoch milliseconds, ISO strings with a
 *    time. These are real moments, so a Date is the right tool, and they are
 *    shown in the viewer's local time.
 *
 * formatDate() takes either and does the right thing for each.
 *
 * The only Date objects for calendar dates are at the edges, and both are
 * here: todayDateString() reads the clock, and formatLocalDate() hands a day
 * to toLocaleDateString() for display.
 */
import {
  format,
  parseISO,
  isValid,
  addMonths as addMonthsToInstant,
  startOfMonth,
} from "date-fns";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const pad2 = (n) => String(n).padStart(2, "0");
const pad4 = (n) => String(n).padStart(4, "0");

// ===========================================================================
// CALENDAR DATES
// ===========================================================================

export const isLeapYear = (year) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

export const daysInMonth = (year, month) => {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  if (month === 4 || month === 6 || month === 9 || month === 11) return 30;
  return 31;
};

/**
 * "2026-05-04" → { year: 2026, month: 5, day: 4 }.
 *
 * A trailing time ("2026-05-04T00:00:00.000Z") is ignored, not converted:
 * only the calendar part is read. Anything else, or an impossible date
 * (2026-02-30), → null.
 */
export const parseDateString = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/.exec(
    String(value ?? "").trim(),
  );
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
};

const buildDateString = ({ year, month, day }) =>
  `${pad4(year)}-${pad2(month)}-${pad2(day)}`;

/** "2026-05-04" and nothing more: a calendar date, not an instant. */
export const isPlainDateString = (value) =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) &&
  parseDateString(value) !== null;

/** Anything date-like → "yyyy-mm-dd", or "" if it isn't a real date. */
export const toDateString = (value) => {
  const parts = parseDateString(value);
  return parts ? buildDateString(parts) : "";
};

/** Today on the viewer's own calendar, as "yyyy-mm-dd". */
export const todayDateString = () => {
  const now = new Date();
  return buildDateString({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  });
};

// ---- Comparing ------------------------------------------------------------

/** The earlier of two dates. An invalid side is ignored; both invalid → "". */
export const minDate = (a, b) => {
  const x = toDateString(a);
  const y = toDateString(b);
  if (!x) return y;
  if (!y) return x;
  return x <= y ? x : y;
};

/** The later of two dates. An invalid side is ignored; both invalid → "". */
export const maxDate = (a, b) => {
  const x = toDateString(a);
  const y = toDateString(b);
  if (!x) return y;
  if (!y) return x;
  return x >= y ? x : y;
};

/** start <= date <= end, inclusive. False if any of the three is invalid. */
export const isWithin = (date, start, end) => {
  const d = toDateString(date);
  const s = toDateString(start);
  const e = toDateString(end);
  if (!d || !s || !e) return false;
  return d >= s && d <= e;
};

// ---- Adjusting ------------------------------------------------------------

/** Move by days, rolling over month and year ends. */
export const addDays = (value, count) => {
  const parts = parseDateString(value);
  if (!parts) return "";
  let { year, month, day } = parts;
  day += count;
  while (day > daysInMonth(year, month)) {
    day -= daysInMonth(year, month);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  while (day < 1) {
    month -= 1;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    day += daysInMonth(year, month);
  }
  return buildDateString({ year, month, day });
};

/**
 * Move by whole months. The day is kept, or pulled back to the end of a
 * shorter month: addMonths("2026-01-31", 1) → "2026-02-28".
 */
export const addMonths = (value, count) => {
  const parts = parseDateString(value);
  if (!parts) return "";
  const index = parts.year * 12 + (parts.month - 1) + count;
  const year = Math.floor(index / 12);
  const month = index - year * 12 + 1;
  const day = Math.min(parts.day, daysInMonth(year, month));
  return buildDateString({ year, month, day });
};

export const addYears = (value, count) => addMonths(value, count * 12);

// ---- Months ---------------------------------------------------------------

/** "2026-05" or "2026-05-04" → { year, month }, otherwise null. */
const parseYearMonth = (value) => {
  const match = /^(\d{4})-(\d{2})(?:$|[-T ])/.exec(String(value ?? "").trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
};

/** "2026-05-04" → "2026-05". Sorts and compares like the full date. */
export const monthKey = (value) => {
  const ym = parseYearMonth(value);
  return ym ? `${pad4(ym.year)}-${pad2(ym.month)}` : "";
};

/** "2026-05-04" → "2026-05-01". */
export const monthStartString = (value) => {
  const key = monthKey(value);
  return key ? `${key}-01` : "";
};

/** "2026-02-10" → "2026-02-28". */
export const monthEndString = (value) => {
  const ym = parseYearMonth(value);
  return ym
    ? buildDateString({ ...ym, day: daysInMonth(ym.year, ym.month) })
    : "";
};

/**
 * One option per month between two dates, inclusive, oldest first:
 * { label: "May 2026", value: "2026-05", date_value: "2026-05-01" }.
 * Reversed endpoints are swapped.
 *
 * Calendar dates go through year/month integers. Instants (timestamps, Date
 * objects) fall back to date-fns, which is correct for those.
 */
export const generateMonthYearOptions = (minValue, maxValue) => {
  const buildOption = (year, month) => {
    const value = `${pad4(year)}-${pad2(month)}`;
    return {
      label: `${MONTHS_LONG[month - 1]} ${year}`,
      value,
      date_value: `${value}-01`,
    };
  };

  let from = parseYearMonth(minValue);
  let to = parseYearMonth(maxValue);

  if (from && to) {
    if (
      from.year > to.year ||
      (from.year === to.year && from.month > to.month)
    ) {
      [from, to] = [to, from];
    }
    const result = [];
    let { year, month } = from;
    while (year < to.year || (year === to.year && month <= to.month)) {
      result.push(buildOption(year, month));
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    }
    return result;
  }

  // Instants, not calendar dates.
  const startInstant = toInstant(minValue);
  const endInstant = toInstant(maxValue);
  if (!startInstant || !endInstant) return [];
  let start = startOfMonth(startInstant);
  let end = startOfMonth(endInstant);
  if (start > end) [start, end] = [end, start];
  const result = [];
  for (
    let current = start;
    current <= end;
    current = addMonthsToInstant(current, 1)
  ) {
    result.push(buildOption(current.getFullYear(), current.getMonth() + 1));
  }
  return result;
};

// ---- People ---------------------------------------------------------------

/**
 * Age in whole years on a given day (today by default). A birthday on
 * February 29 counts as reached on March 1 in non-leap years. null if either
 * date is invalid.
 */
export const ageOn = (dateOfBirth, onDate = todayDateString()) => {
  const born = parseDateString(dateOfBirth);
  const on = parseDateString(onDate);
  if (!born || !on) return null;
  let age = on.year - born.year;
  if (on.month < born.month || (on.month === born.month && on.day < born.day)) {
    age -= 1;
  }
  return age;
};

// ---- Typed input ----------------------------------------------------------

/**
 * "05/04/2026" (or "5/4/2026") as typed by a person → "2026-05-04".
 * "" if it isn't a real date, so this doubles as the validity check.
 */
export const parseUSDate = (value) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(
    String(value ?? "").trim(),
  );
  if (!match) return "";
  return toDateString(`${match[3]}-${pad2(match[1])}-${pad2(match[2])}`);
};
/**
 * "Oct 2026", "October 2026" or "Sept 2026" → "2026-10" (a monthKey).
 * "" otherwise. For reading month labels back, e.g. to sort a column of them.
 */
export const parseMonthYear = (value) => {
  const match = /^([A-Za-z]+)\.?\s+(\d{4})$/.exec(String(value ?? "").trim());
  if (!match) return "";
  const name = match[1].toLowerCase();
  if (name.length < 3) return "";
  const index = MONTHS_LONG.findIndex((m) => m.toLowerCase().startsWith(name));
  return index < 0 ? "" : `${match[2]}-${pad2(index + 1)}`;
};

// ===========================================================================
// INSTANTS
// ===========================================================================

/**
 * Anything that names a moment → a Date, or null. Epoch milliseconds may
 * arrive as numbers or numeric strings.
 *
 * Not for calendar dates: formatDate() checks for those first.
 */
const toInstant = (value) => {
  if (!value && value !== 0) return null;
  if (value instanceof Date)
    return isValid(value) ? new Date(value.getTime()) : null;
  if (typeof value === "number") {
    const parsed = new Date(value);
    return isValid(parsed) ? parsed : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const parsed = new Date(Number(trimmed));
      return isValid(parsed) ? parsed : null;
    }
    const isoParsed = parseISO(trimmed);
    if (isValid(isoParsed)) return isoParsed;
    const fallback = new Date(trimmed);
    return isValid(fallback) ? fallback : null;
  }
  return null;
};

/**
 * The calendar day an instant falls on, in the viewer's time zone, as
 * "yyyy-mm-dd". For comparing a timestamp against a calendar date.
 */
export const dateStringOf = (value) => {
  const instant = toInstant(value);
  return instant
    ? buildDateString({
        year: instant.getFullYear(),
        month: instant.getMonth() + 1,
        day: instant.getDate(),
      })
    : "";
};
/**
 * An instant as epoch milliseconds, or null. Accepts numbers, numeric
 * strings and ISO strings. For sorting timestamp columns.
 */
export const toTimestamp = (value) => {
  const instant = toInstant(value);
  return instant ? instant.getTime() : null;
};
/** An instant with its time: "Oct 10, 2026 9:41 PM". */
export const formatTimestamp = (value, pattern = "MMM d, yyyy h:mm a") => {
  const instant = toInstant(value);
  return instant ? format(instant, pattern) : "";
};

// ===========================================================================
// DISPLAY
// ===========================================================================

/**
 * A calendar date against a date-fns pattern, from the string parts.
 * Returns null for a pattern not listed, so formatDate() can warn rather
 * than produce a wrong string. Add patterns here as they're needed.
 */
const formatDateParts = ({ year, month, day }, pattern) => {
  const y = pad4(year);
  const mm = pad2(month);
  const dd = pad2(day);
  switch (pattern) {
    case "MMM dd, yyyy":
      return `${MONTHS_SHORT[month - 1]} ${dd}, ${y}`;
    case "MMMM dd, yyyy":
      return `${MONTHS_LONG[month - 1]} ${dd}, ${y}`;
    case "MMM d, yyyy":
      return `${MONTHS_SHORT[month - 1]} ${day}, ${y}`;
    case "MMM d, yy":
      return `${MONTHS_SHORT[month - 1]} ${day}, ${y.slice(-2)}`;
    case "MMMM d, yyyy":
      return `${MONTHS_LONG[month - 1]} ${day}, ${y}`;
    case "MMM dd":
      return `${MONTHS_SHORT[month - 1]} ${dd}`;
    case "MMM d":
      return `${MONTHS_SHORT[month - 1]} ${day}`;
    case "MM/dd/yyyy":
      return `${mm}/${dd}/${y}`;
    case "M/d/yyyy":
      return `${month}/${day}/${y}`;
    case "dd/MM/yyyy":
      return `${dd}/${mm}/${y}`;
    case "yyyy-MM-dd":
      return `${y}-${mm}-${dd}`;
    case "MMM yyyy":
      return `${MONTHS_SHORT[month - 1]} ${y}`;
    case "MMMM yyyy":
      return `${MONTHS_LONG[month - 1]} ${y}`;
    case "yyyy-MM":
      return `${y}-${mm}`;
    case "yyyy":
      return y;
    default:
      return null;
  }
};

/**
 * The one display function for dates, calendar or instant.
 *
 *   formatDate("2026-05-04")              → "May 04, 2026"  (never shifts)
 *   formatDate(1788100000000)             → that moment's local date
 *   formatDate(value, "MM/dd/yyyy")       → any date-fns pattern
 *
 * Empty input → "". Something unrecognisable → shown as is.
 */
export const formatDate = (value, pattern = "MMM dd, yyyy") => {
  if (!value && value !== 0) return "";

  if (isPlainDateString(value)) {
    const formatted = formatDateParts(parseDateString(value), pattern);
    if (formatted !== null) return formatted;
    console.warn(
      `formatDate: unsupported pattern "${pattern}" for a calendar date`,
    );
  }

  try {
    const instant = toInstant(value);
    return instant ? format(instant, pattern) : String(value);
  } catch {
    return String(value);
  }
};

export const formatMonthYear = (value, pattern = "MMM yyyy") =>
  formatDate(value, pattern);

export const formatYear = (value, pattern = "yyyy") =>
  formatDate(value, pattern);

/** "2026-05-04" → "5/4/2026". */
export const displayDate = (value) =>
  formatDate(toDateString(value), "M/d/yyyy");

/**
 * A calendar date through toLocaleDateString(), for callers that pass Intl
 * options. The Date is built from the day's own parts at noon, so no time
 * zone can move it to another day.
 */
export const formatLocalDate = (value, formatOption = null) => {
  const parts = parseDateString(value);
  if (!parts) return "";
  const date = new Date(parts.year, parts.month - 1, parts.day, 12);
  return formatOption
    ? date.toLocaleDateString("en-US", formatOption)
    : date.toLocaleDateString("en-US");
};
